import { OkamiHttpGatewayError } from '@/api/errors.ts'
import {
  getCustomListControllerFetchCustomListWorksQueryOptions,
  useCustomListControllerFetchCustomListWorks,
} from '@/api/generated/okami.ts'
import { CollectionDetails } from '@/routes/_app/collections/-components/collection-details.tsx'
import { CollectionListLoading } from '@/routes/_app/collections/-components/collection-list-loading.tsx'
import { CollectionWorkCard } from '@/routes/_app/collections/-components/collection-work-card.tsx'
import { MarkWorksAsReadDialog } from '@/routes/_app/works/-components/mark-work-as-read.tsx'
import { CACHE_TIME } from '@/utils/helpers.ts'
import { createFileRoute } from '@tanstack/react-router'
import { AnimatePresence, motion } from 'framer-motion'
import { Suspense } from 'react'
import { toast } from 'sonner'

export const Route = createFileRoute('/_app/collections/$collectionId')({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    return context.queryClient.ensureQueryData(
      getCustomListControllerFetchCustomListWorksQueryOptions(params.collectionId),
    )
  },
  head: ({ loaderData }) => ({
    meta: [
      {
        title: `${loaderData?.title} | Okami`,
      },
    ],
  }),
})

function RouteComponent() {
  const { collectionId } = Route.useParams()
  const navigate = Route.useNavigate()

  const {
    isLoading,
    data: collection,
    error,
    isPending,
  } = useCustomListControllerFetchCustomListWorks(collectionId, {
    query: {
      enabled: !!collectionId,
      staleTime: CACHE_TIME.FIFTEEN_MINUTES,
    },
  })

  if (!!error) {
    toast.error('Erro ao carregar obras')

    if (error instanceof OkamiHttpGatewayError && error.isResourceNotFoundError()) {
      return navigate({ to: '/collections', replace: true })
    }
  }

  if (isLoading || isPending) {
    return <CollectionListLoading />
  }

  return (
    <>
      <MarkWorksAsReadDialog />

      <div className="space-y-3">
        <section className="flex flex-1 w-full justify-between flex-col md:flex-row gap-4 items-center">
          <Suspense>
            <CollectionDetails />
          </Suspense>
        </section>

        {collection?.works?.length ? (
          <div className="flex flex-col justify-between mt-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 2xl:grid-cols-6">
              <AnimatePresence>
                {collection.works.map((work) => (
                  <motion.div
                    key={work.id}
                    layout
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.5 }}
                    transition={{ duration: 0.2, ease: 'easeInOut' }}
                  >
                    <CollectionWorkCard work={work} />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6">
            <section className="col-span-full">
              <div className="flex flex-col items-center justify-center gap-4 p-4 mt-10">
                <span className="text-muted-foreground  text-center">
                  Nada por aqui, adicione uma obra para começar
                </span>
              </div>
            </section>
          </div>
        )}
      </div>
    </>
  )
}
